import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CognitoService } from '../cognito.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly excludedUrls = [`${environment.cognitoDomain}/oauth2/token`];
  private readonly protectedUrls = [
    environment.operationsApi,
    environment.sourcingApi,
    environment.salesApi,
    environment.productApi,
    environment.warehouseApi,
    environment.financialsApi,
    environment.mailingApi,
    environment.notificationsEndpoint
  ];
  private redirectingToLogin = false;

  constructor(
    private readonly authService: CognitoService,
    private readonly router: Router
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.excludedUrls.some(url => request.url.includes(url))) {
      return next.handle(request);
    }

    if (!this.isProtectedRequest(request)) {
      return next.handle(request);
    }

    if (request.headers.has('Authorization')) {
      return next.handle(request).pipe(
        catchError((error) => this.handleAuthError(error))
      );
    }

    return from(this.authService.getToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return from(this.redirectToLogin()).pipe(
            switchMap(() => throwError(() => new Error('Sesión expirada o inválida')))
          );
        }

        return next.handle(
          request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
          })
        ).pipe(
          catchError((error) => this.handleAuthError(error))
        );
      })
    );
  }

  private isProtectedRequest(request: HttpRequest<unknown>): boolean {
    return this.protectedUrls.some((url) => !!url && request.url.startsWith(url));
  }

  private async redirectToLogin(): Promise<void> {
    if (this.redirectingToLogin) return;
    this.redirectingToLogin = true;

    const currentUrl = this.router.url;
    if (currentUrl && currentUrl !== '/auth' && !currentUrl.startsWith('/callback')) {
      sessionStorage.setItem('auth_return_url', currentUrl);
    }

    try {
      await this.authService.signInWithHostedUi();
    } catch (error) {
      console.error('No se pudo redirigir a Cognito:', error);
      await this.router.navigateByUrl('/auth', { replaceUrl: true });
    } finally {
      this.redirectingToLogin = false;
    }
  }

  private handleAuthError(error: any): Observable<never> {
    if (error?.status !== 401 && error?.status !== 403) {
      return throwError(() => error);
    }

    return from(this.handleUnauthorizedResponse(error.status)).pipe(
      switchMap(() => throwError(() => error))
    );
  }

  private async handleUnauthorizedResponse(status: number): Promise<void> {
    this.authService.clearAuthorizationCache();

    if (status === 401) {
      await this.redirectToLogin();
      return;
    }

    const stillAuthorized = await this.authService.revalidateAuthorization();
    if (stillAuthorized) {
      return;
    }

    sessionStorage.setItem('auth_access_denied', '1');
    await this.authService.signOut();
  }
}
